import { Component, Input, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, Validators } from '@angular/forms';
import { CommonService } from '@shared/services/common.service';
import { Subject, takeUntil } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import { DynamicFormBuilderService } from '@modules/dynamic-form-builder/services/dynamic-form-module.service';
import { fieldTypes } from '@constants/constant';

@Component({
  selector: 'app-option-list',
  templateUrl: './option-list.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OptionListComponent),
      multi: true
    }
  ]
})
export class OptionListComponent implements ControlValueAccessor {
  @Input() field: any;
  inputForm: any;
  inputfield: any;
  selectForm: any;
  selectfield: any;
  features: { text1: string; text2: string ; text3: string; text4 : string }[] = [];
  featureText1: string = '';
  featureText2: string = '';
  taxTypeForm: any;
  taxTypeField: any;
  featureText3: string = '';
  featureText4: string = '';
  taxTypeslist: any;

  private destroy$ = new Subject<void>();
  

  constructor(
    public readonly cs: CommonService,
    private readonly formService: DynamicFormBuilderService,
  ) { 
  }

  onChange = (_features: { text1: string; text2: string }[]) => {};
  onTouched = () => {};

  ngOnInit() {
    if ( this.cs.isBrowser) {
      const field1 = cloneDeep(this.field?.featureLabels?.field1);
      this.inputfield = field1;
  
      this.inputForm = this.formService.toFormGroup({
        sections: [{ title: '', fields: [field1] }]
      });
  
      const control1 = this.inputForm.get(field1.name);
      if (control1) {
        control1.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
          this.featureText1 = value;
          this.onChange(this.getAllFeatures());
        });
      }
  
      const field2 = this.field?.featureLabels?.field2;
      if (field2?.type === fieldTypes.SELECT) {
        this.selectfield = field2;
  
        this.selectForm = this.formService.toFormGroup({
          sections: [{ title: '', fields: [field2] }]
        });
  
        const control = this.selectForm.get(field2.name);
        if (control) {
          control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
            this.featureText2 = value;
            const taxTypes = this.field?.getTaxTypesByCountry(value);
            this.taxTypeField.options = taxTypes;
            const taxTypeFormControl = this.taxTypeForm.get(this.taxTypeField.name);
            if (taxTypeFormControl) {
              taxTypeFormControl.reset();
            }
            this.onChange(this.getAllFeatures());
          });
        }
      }
      this.field?.getTaxTypesList()
      const field3 = cloneDeep(this.field?.featureLabels?.field3);
      this.taxTypeField = field3;
      this.taxTypeForm = this.formService.toFormGroup({
        sections: [{ title: '', fields: [field3] }]
      });
  
      const taxTypeControl = this.taxTypeForm.get(this.taxTypeField.name);
      if (taxTypeControl) {
        taxTypeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
          this.featureText3 = value;
          this.featureText4 = this.getTaxTypeLabel(value);
          this.onChange(this.getAllFeatures());
        });
      }
    }
  }
  updateFieldValidatorsBasedOnFeatures(): void {
    const field1Control = this.inputForm?.get(this.inputfield?.name);
    const field2Control = this.selectForm?.get(this.selectfield?.name);
    const field3Control = this.taxTypeForm?.get(this.taxTypeField?.name);
  
    if (this.features.length > 0) {
      field1Control?.clearValidators();
      field2Control?.clearValidators();
      field3Control?.clearValidators();
    } else {
      if (field1Control) field1Control.setValidators([Validators.required]);
      if (field2Control) field2Control.setValidators([Validators.required]);
      if (field3Control) field3Control.setValidators([Validators.required]);
    }
  
    field1Control?.updateValueAndValidity();
    field2Control?.updateValueAndValidity();
    field3Control?.updateValueAndValidity();
  }
  getTaxTypeLabel(code: string): string {
    const option = this.taxTypeField?.options?.find(opt => opt[this.taxTypeField?.valueKey] === code);
    return option ? option[this.taxTypeField?.labelKey] : code; // fallback to code if label not found
  }
  
  addFeature() {
    if (this.featureText1.trim() && this.featureText2.trim()) {
      this.features.push({
        text1: this.featureText1.trim(),
        text2: this.featureText2.trim(),
        text3: this.featureText3.trim(),
        text4: this.featureText4.trim()
      });
      this.onChange(this.features);
      const field1Name = this.inputfield?.name;
      const field2Name = this.selectfield?.name;
      const field3Name = this.taxTypeField?.name;
      if (field1Name && this.inputForm?.get(field1Name)) {
        this.inputForm.get(field1Name)?.reset('');
      }
      if (field2Name && this.selectForm?.get(field2Name)) {
        this.selectForm.get(field2Name)?.reset('');
      }
      if (field3Name && this.taxTypeForm?.get(field3Name)) {
        this.taxTypeForm.get(field3Name)?.reset('');
      }
      this.featureText1 = '';
      this.featureText2 = '';
      this.featureText3 = '';
      this.featureText4 = '';
      this.updateFieldValidatorsBasedOnFeatures();

    }
  }
  
  removeFeature(idx: number) {
    this.features.splice(idx, 1);
    this.onChange(this.getAllFeatures());
    this.updateFieldValidatorsBasedOnFeatures();

  }

  writeValue(features: { text1: string; text2: string; text3: string; text4: string }[]): void {
    this.features = features || [];
    this.updateFieldValidatorsBasedOnFeatures();
  }  

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  getAllFeatures(): { text1: string; text2: string; text3: string; text4: string }[] {
    const draft = this.featureText1?.trim() && this.featureText2?.trim() && this.featureText3?.trim()
      ? [{
          text1: this.featureText1.trim(),
          text2: this.featureText2.trim(),
          text3: this.featureText3.trim(),
          text4: this.featureText4.trim()
        }]
      : [];
  
    return [...this.features, ...draft];
  }
  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}